import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import i18n from 'live/services/i18n'
import { Modal, Button } from '@intelie/ui'
import { WidgetContext } from 'live/context/widget'
import { WidgetEditorContext } from 'live/context/widget-editor'
import { DashboardContext } from 'live/context/dashboard'
import { ColorStyle, CustomSettingsByChannel, LiveMultiValuesMeta, resolveNewChannelDefaults, ViewMode } from '../api'
import { clearViewerOverride, saveViewerOverride } from '../viewerOverride'
import { ChannelsTab } from './ChannelsTab'
import { DisplayTab } from './DisplayTab'

interface WidgetContextActions {
    changeConfig?: (field: string, value: unknown, options?: unknown) => void
}

type Tab = 'channels' | 'display'

type Props = {
    show: boolean
    onHide: () => void
    title: string
    meta: LiveMultiValuesMeta
    widgetId: string | number
    // effective - what this viewer currently sees: their own browser-local override layered over the
    // canonical setup, or just the canonical setup in edit mode / for a viewer who's never customized
    selectedChannels: CustomSettingsByChannel[]
    excludedChannels: string[]
    viewMode: ViewMode
    colorStyle: ColorStyle
    colorTextEnabled: boolean
    // the widget's canonical setup, as last saved from its own editor - what "Restore Defaults" (view
    // mode only) falls back to
    canonicalSelectedChannels: CustomSettingsByChannel[]
    canonicalExcludedChannels: string[]
    canonicalViewMode: ViewMode
    canonicalColorStyle: ColorStyle
    canonicalColorTextEnabled: boolean
    boxSize: string
}

export const SettingsModal = React.memo(function SettingsModal({
    show,
    onHide,
    title,
    meta,
    widgetId,
    selectedChannels,
    excludedChannels,
    viewMode,
    colorStyle,
    colorTextEnabled,
    canonicalSelectedChannels,
    canonicalExcludedChannels,
    canonicalViewMode,
    canonicalColorStyle,
    canonicalColorTextEnabled,
    boxSize
}: Props): JSX.Element {
    // contexts
    const widgetContext = useContext(WidgetContext)
    const dashboardContext = useContext(DashboardContext)
    const widgetEditorContext = (useContext(WidgetEditorContext) as unknown) as { data: unknown }

    const isWidgetEditMode = widgetEditorContext?.data != null
    const hasChannels = meta.channels?.length > 0

    const [activeTab, setActiveTab] = useState<Tab>(hasChannels ? 'channels' : 'display')
    const [updatedSelectedChannels, setUpdatedSelectedChannels] = useState<CustomSettingsByChannel[]>([])
    const [updatedExcludedChannels, setUpdatedExcludedChannels] = useState<string[]>([])
    const [updatedViewMode, setUpdatedViewMode] = useState<ViewMode>(viewMode)
    const [updatedBoxSize, setUpdatedBoxSize] = useState<string>(boxSize)
    const [updatedColorStyle, setUpdatedColorStyle] = useState<ColorStyle>(colorStyle)
    const [updatedColorTextEnabled, setUpdatedColorTextEnabled] = useState<boolean>(colorTextEnabled)

    const resolvedUpdatedSelectedChannels = useMemo(
        () => resolveNewChannelDefaults(meta.channels, updatedSelectedChannels, updatedExcludedChannels).selectedChannels,
        [meta.channels, updatedSelectedChannels, updatedExcludedChannels]
    )

    // resolved the same way as the draft above, so "does the draft match canonical" (in handleSave)
    // compares like with like. Canonical is only ever updated by the widget's own background
    // resolution effect (LiveMultiValuesWidget.tsx), which runs after render - so on a fresh viewer
    // opening Settings before that effect has caught up, raw canonicalSelectedChannels can still be
    // missing a dynamic channel that resolvedUpdatedSelectedChannels already includes. Comparing
    // against unresolved canonical would read that gap as a real edit and spuriously create a viewer
    // override from an untouched open+Save.
    const resolvedCanonicalSelectedChannels = useMemo(
        () =>
            resolveNewChannelDefaults(meta.channels, canonicalSelectedChannels ?? [], canonicalExcludedChannels ?? [])
                .selectedChannels,
        [meta.channels, canonicalSelectedChannels, canonicalExcludedChannels]
    )

    useEffect(() => {
        if (show) {
            setActiveTab(hasChannels ? 'channels' : 'display')
            setUpdatedSelectedChannels(selectedChannels ?? [])
            setUpdatedExcludedChannels(excludedChannels ?? [])
            setUpdatedViewMode(viewMode)
            setUpdatedBoxSize(boxSize)
            setUpdatedColorStyle(colorStyle)
            setUpdatedColorTextEnabled(colorTextEnabled)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show])

    const handleSave = useCallback(async (): Promise<void> => {
        const widgetContextActions = ((widgetContext as unknown) as { actions: WidgetContextActions }).actions

        // boxSize (Density) always stays canonical - it's the same underlying setting as the widget's
        // native "Box size" Chart Options control, which has no per-viewer concept of its own either
        widgetContextActions.changeConfig('boxSize', updatedBoxSize)

        if (isWidgetEditMode) {
            // the setup here IS the widget's setup - written straight to the canonical config every
            // viewer, and "Restore Defaults", fall back to. excludedChannels is written before
            // selectedChannels deliberately: changeConfig applies each field as its own update, so the
            // two are briefly inconsistent with each other mid-save. If selectedChannels landed first,
            // a channel just removed here would - for one render - be in neither array, and
            // LiveMultiValuesWidget's own default-selection effect (which only ever adds, never excludes)
            // would see it as newly undecided and re-add it, undoing this exact Remove. Writing
            // excludedChannels first means that same channel is already accounted for by the time
            // selectedChannels drops it, so that effect never sees a state where it looks undecided.
            widgetContextActions.changeConfig('excludedChannels', updatedExcludedChannels)
            widgetContextActions.changeConfig('selectedChannels', resolvedUpdatedSelectedChannels)
            widgetContextActions.changeConfig('viewMode', updatedViewMode)
            widgetContextActions.changeConfig('colorStyle', updatedColorStyle)
            widgetContextActions.changeConfig('colorTextEnabled', updatedColorTextEnabled)
        } else if (widgetId != null) {
            // a view-mode change is this viewer's own configuration - kept browser-local (see
            // viewerOverride.ts) so it never affects the canonical setup other viewers, or the editor,
            // see. If the draft exactly matches canonical (typically right after Restore Defaults),
            // clear the override entirely instead of saving a copy of it - a cleared override keeps
            // tracking canonical if it changes again later, where a frozen copy would silently stop.
            const matchesCanonical =
                JSON.stringify(resolvedUpdatedSelectedChannels) === JSON.stringify(resolvedCanonicalSelectedChannels) &&
                JSON.stringify(updatedExcludedChannels) === JSON.stringify(canonicalExcludedChannels ?? []) &&
                updatedViewMode === canonicalViewMode &&
                updatedColorStyle === canonicalColorStyle &&
                updatedColorTextEnabled === canonicalColorTextEnabled

            if (matchesCanonical) {
                clearViewerOverride(widgetId)
            } else {
                saveViewerOverride(widgetId, {
                    channels: { selectedChannels: resolvedUpdatedSelectedChannels, excludedChannels: updatedExcludedChannels },
                    viewMode: updatedViewMode,
                    colorStyle: updatedColorStyle,
                    colorTextEnabled: updatedColorTextEnabled
                })
            }
        }

        // restart queries to apply the new channel selection, and provide the live_widget_multi_values_identity lookup values via request interceptor -
        // dashboardContext.actions is only present for a widget embedded in a real dashboard, not the
        // widget's own dedicated editor (there's no dashboard to restart a query within there), so this
        // has to be optional - unguarded, it threw and aborted handleSave before onHide ever ran, which
        // is exactly why Save appeared to silently do nothing (or only partially apply) from the editor
        const index = ((widgetContext as unknown) as { widgetIndex: number }).widgetIndex
        dashboardContext.actions?.stop?.(index.toString())
        dashboardContext.actions?.start?.(index)

        return onHide()
    }, [
        resolvedUpdatedSelectedChannels,
        resolvedCanonicalSelectedChannels,
        updatedExcludedChannels,
        updatedViewMode,
        updatedBoxSize,
        updatedColorStyle,
        updatedColorTextEnabled,
        isWidgetEditMode,
        widgetId,
        canonicalExcludedChannels,
        canonicalViewMode,
        canonicalColorStyle,
        canonicalColorTextEnabled,
        widgetContext,
        dashboardContext,
        onHide
    ])

    // view mode only: forgets this viewer's own customization entirely, reverting the local draft to
    // exactly what the widget's own editor last saved as its setup. Save (or Cancel) still decides
    // whether this actually takes effect - see the matchesCanonical check above for what Save then does
    const handleRestoreDefaults = useCallback((): void => {
        setUpdatedSelectedChannels(canonicalSelectedChannels ?? [])
        setUpdatedExcludedChannels(canonicalExcludedChannels ?? [])
        setUpdatedViewMode(canonicalViewMode)
        setUpdatedColorStyle(canonicalColorStyle)
        setUpdatedColorTextEnabled(canonicalColorTextEnabled)
        // boxSize has no per-viewer override to begin with - it already has its own restore path via
        // Live's native "Restore Chart Defaults" affordance, so it's left untouched here
    }, [canonicalSelectedChannels, canonicalExcludedChannels, canonicalViewMode, canonicalColorStyle, canonicalColorTextEnabled])

    // clears the sticky memory of explicit Removes for dynamic (query `selected: true`) channels, so
    // they're re-evaluated fresh against the query - but "pristine" means something different in each
    // context. In the editor there's only one layer, so a full reset also un-selects any dynamic
    // channel currently selected, letting every one of them (not just the removed ones) be freshly
    // decided next. In view mode, this viewer's own selections are worth keeping - only their removals
    // are forgiven; "Restore Defaults" above is what discards a viewer's own selections entirely.
    const handleClearExclusionList = useCallback((): void => {
        if (isWidgetEditMode) {
            const dynamicMnemonics = new Set(
                (meta.channels ?? []).filter(channel => channel.selected === true).map(channel => channel.mnemonic)
            )
            setUpdatedSelectedChannels(current => current.filter(channel => !dynamicMnemonics.has(channel.mnemonic)))
            setUpdatedExcludedChannels(current => current.filter(mnemonic => !dynamicMnemonics.has(mnemonic)))
        } else {
            setUpdatedExcludedChannels([])
        }
    }, [isWidgetEditMode, meta.channels])

    return (
        <Modal show={show} size="md" onHide={onHide} closable={true} onSave={handleSave}>
            <WrapperModalStyles>
                <Modal.Header title={i18n('Settings')} closeButton={true} />

                <Modal.Content className="settings-modal-content">
                    <Tabs>
                        {hasChannels && (
                            <TabButton
                                className={activeTab === 'channels' ? 'active' : ''}
                                onClick={() => setActiveTab('channels')}
                            >
                                {i18n('Channels')}
                            </TabButton>
                        )}
                        <TabButton
                            className={activeTab === 'display' ? 'active' : ''}
                            onClick={() => setActiveTab('display')}
                        >
                            {i18n('Display')}
                        </TabButton>
                    </Tabs>
                    <TabBody>
                        {activeTab === 'channels' ? (
                            <ChannelsTab
                                meta={meta}
                                selectedChannels={resolvedUpdatedSelectedChannels}
                                excludedChannels={updatedExcludedChannels}
                                colorStyle={updatedColorStyle}
                                colorTextEnabled={updatedColorTextEnabled}
                                onChange={next => {
                                    setUpdatedSelectedChannels(next.selectedChannels)
                                    setUpdatedExcludedChannels(next.excludedChannels)
                                }}
                            />
                        ) : (
                            <DisplayTab
                                viewMode={updatedViewMode}
                                onChangeViewMode={setUpdatedViewMode}
                                boxSize={updatedBoxSize}
                                onChangeBoxSize={setUpdatedBoxSize}
                                colorStyle={updatedColorStyle}
                                onChangeColorStyle={setUpdatedColorStyle}
                                colorTextEnabled={updatedColorTextEnabled}
                                onChangeColorTextEnabled={setUpdatedColorTextEnabled}
                            />
                        )}
                    </TabBody>
                </Modal.Content>
                <Modal.Footer>
                    <FooterActions>
                        {hasChannels && (
                            <Button onClick={handleClearExclusionList}>{i18n('Clear Exclusion List')}</Button>
                        )}
                        {!isWidgetEditMode && <Button onClick={handleRestoreDefaults}>{i18n('Restore Defaults')}</Button>}
                    </FooterActions>
                </Modal.Footer>
            </WrapperModalStyles>
        </Modal>
    )
})

// header, content and footer share the same flat background convention used by the other widget modals
const WrapperModalStyles = styled.div`
    .settings-modal-content {
        // clamped against the viewport so the modal shrinks on a smaller window instead of
        // overflowing it - these were previously fixed pixel values with no responsive fallback
        height: min(539px, 80vh);
        width: min(880.5px, 92vw);
        padding: 0;
        display: flex;
        flex-direction: column;
        background-color: var(--gray-200);
    }

    header,
    footer {
        font-size: 16px;
        line-height: 24px;
        background-color: var(--gray-0);
    }

    header {
        height: 56px;
        padding: 8px 8px 8px 24px;
        border-bottom: 0.5px solid var(--gray-300);
        font-weight: 600;
        color: var(--gray-1000);

        button {
            height: 40px;
            width: 40px;
            padding: 0;

            i {
                color: var(--gray-1000);
            }
        }
    }

    footer {
        height: 72px;
        padding: 16px 24px 16px 24px;
        border-top: 0.5px solid var(--gray-300);

        button {
            height: 40px;
            box-shadow: none;
        }

        button:focus {
            outline: none;
        }
    }
`

const FooterActions = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`

const Tabs = styled.div`
    flex-shrink: 0;
    display: flex;
    gap: 4px;
    padding: 10px 24px 0;
    background-color: var(--gray-0);
    border-bottom: 0.5px solid var(--gray-300);
`

const TabButton = styled.button`
    font-size: 13px;
    line-height: 20px;
    font-weight: 600;
    padding: 8px 4px 12px;
    border: none;
    border-bottom: 2px solid transparent;
    background: none;
    color: var(--gray-800);
    cursor: pointer;

    &.active {
        color: var(--blue-500);
        border-bottom-color: var(--blue-500);
    }
`

const TabBody = styled.div`
    flex: 1;
    min-height: 0;
`
